import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Map } from 'lucide-react';
import { Button, Dialog, DialogShell, DialogShellHeader, DialogShellBody } from '@bliks-io/ui';

/**
 * The map's frame, and the two things it can hold instead of a diagram.
 *
 * THE ORDERING RULE FOR THIS MODULE, stated once here and referenced from the
 * other files: the frame lives ABOVE the lazy boundary and the chunk ships only
 * a body, so ONE dialog instance spans the whole sequence — click → module →
 * data → diagram. It is load-bearing twice over. A frame that arrived with the
 * chunk would unmount the one already on screen, replaying Radix's enter
 * animation mid-open and firing a close-autofocus for a dialog that is not
 * closing; and a frame mounted only once `open` is already true can never
 * observe the flip that `useOpenerFocusRestore` (in `LazyProcessMapDialog`)
 * needs. Nothing here imports the canvas, so reaching it from the boundary
 * drags no `@xyflow/react` back across.
 */

/**
 * How tall the map is. Viewport-relative rather than a pixel floor: `70vh`
 * inside a `92vh`-capped shell is readable on a laptop without the dialog
 * outgrowing the screen.
 */
export const PROCESS_MAP_BODY_HEIGHT = '70vh';

export function ProcessMapDialogFrame({
  open,
  onOpenChange,
  openerRef,
  children
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Where focus goes when the map closes, from `useOpenerFocusRestore` in
   * `LazyProcessMapDialog`. Passed in rather than captured here: the frame
   * mounts on activation, with `open` already true, so it cannot see the flip
   * itself.
   */
  openerRef: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogShell
        size="xl"
        onCloseAutoFocus={(event) => {
          // Radix's modal content restores focus to its `DialogTrigger`, and
          // this dialog has none — it is opened by an overlay button on a
          // preview card, from state. Left alone, closing the map drops focus
          // on `document.body` and the keyboard user lands back at the top of
          // the host modal. Restore it to whatever opened us instead.
          event.preventDefault();
          openerRef.current?.focus();
        }}
      >
        <DialogShellHeader
          icon={<Map className="h-5 w-5" />}
          iconTone="neutral"
          title={t('processPage.processFlow')}
          subtitle={t('processPage.processFlowDescription')}
          onClose={() => onOpenChange(false)}
        />
        <DialogShellBody padding="p-0">{children}</DialogShellBody>
      </DialogShell>
    </Dialog>
  );
}

/** Something is still on its way — the module, the process, or its layout. */
export function ProcessMapLoadingBody() {
  const { t } = useTranslation();
  return (
    <ProcessMapBodyCenter role="status" label={t('common.loading')}>
      <Loader2 className="h-6 w-6 animate-spin text-fg-subtle" aria-hidden />
    </ProcessMapBodyCenter>
  );
}

/**
 * Something is not coming. Silence here would read as a dead button, so the
 * frame stays open and says so — with the only retry there is.
 *
 * No `onRetry` means the answer is final, and the two go together: the one
 * unretryable failure the map has is `show-process` answering 404, which is
 * also how it answers "not yours". Offering a Retry that can never succeed
 * would be worse than naming the outcome.
 */
export function ProcessMapErrorBody({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <ProcessMapBodyCenter>
      <div className="flex flex-col items-center gap-3">
        <p className="text-sm text-muted-foreground">
          {t(onRetry ? 'errors.unknown' : 'errors.process.notFound')}
        </p>
        {onRetry && (
          <Button variant="outline" size="small" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        )}
      </div>
    </ProcessMapBodyCenter>
  );
}

function ProcessMapBodyCenter({
  children,
  role,
  label
}: {
  children: ReactNode;
  role?: 'status';
  label?: string;
}) {
  return (
    <div
      className="flex items-center justify-center"
      style={{ height: PROCESS_MAP_BODY_HEIGHT }}
      role={role}
      aria-label={label}
    >
      {children}
    </div>
  );
}
